import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Plus, Trash2, Link2 } from 'lucide-react';
import useModal from '../../hooks/useModal';
import { personService, casePartyService, clientService } from '../../services/api';
import ConflictWarningDialog from '../conflicts/ConflictWarningDialog';

const ROLE_OPTIONS = [
  { value: 'opposing_party', label: 'Opposing Party' },
  { value: 'opposing_counsel', label: 'Opposing Counsel' },
  { value: 'co_counsel', label: 'Co-Counsel' },
  { value: 'witness', label: 'Witness' },
  { value: 'expert_witness', label: 'Expert Witness' },
  { value: 'judge', label: 'Judge' },
  { value: 'guardian_ad_litem', label: 'Guardian ad Litem' },
  { value: 'mediator', label: 'Mediator' },
  { value: 'insurer', label: 'Insurer' },
  { value: 'third_party', label: 'Third Party' },
];

const CONTACT_TYPE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone_cell', label: 'Cell Phone' },
  { value: 'phone_home', label: 'Home Phone' },
  { value: 'phone_work', label: 'Work Phone' },
  { value: 'fax', label: 'Fax' },
];

const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'ex_spouse', label: 'Ex-Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'business_partner', label: 'Business Partner' },
  { value: 'employer', label: 'Employer' },
  { value: 'employee', label: 'Employee' },
  { value: 'other', label: 'Other' },
];

const AddPartyModal = ({ isOpen, onClose, caseId, onSuccess }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Form state
  const [personType, setPersonType] = useState('individual');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState('opposing_party');
  const [notes, setNotes] = useState('');
  const [contacts, setContacts] = useState([]);

  // Relationships state
  const [relationships, setRelationships] = useState([]);

  // Client selection state (for role='client')
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientSearchRef = useRef(null);

  // Conflict state
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  const resetForm = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedPerson(null);
    setShowDropdown(false);
    setPersonType('individual');
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setRole('opposing_party');
    setNotes('');
    setContacts([]);
    setRelationships([]);
    setSelectedClient(null);
    setClientSearchQuery('');
    setError('');
    setShowConflictDialog(false);
    setConflictData(null);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetForm();
      // Load initial clients
      clientService.getClients({ limit: 50 }).then(res => {
        const data = res.data?.data || res.data || [];
        setClients(Array.isArray(data) ? data : []);
      }).catch(() => setClients([]));
    }
  }, [isOpen, resetForm]);

  // Debounced server-side client search
  useEffect(() => {
    if (clientSearchRef.current) clearTimeout(clientSearchRef.current);
    clientSearchRef.current = setTimeout(() => {
      setClientSearchLoading(true);
      clientService.getClients({ search: clientSearchQuery || undefined, limit: 50 }).then(res => {
        const data = res.data?.data || res.data || [];
        setClients(Array.isArray(data) ? data : []);
      }).catch(() => setClients([]))
        .finally(() => setClientSearchLoading(false));
    }, 300);
    return () => { if (clientSearchRef.current) clearTimeout(clientSearchRef.current); };
  }, [clientSearchQuery]);

  // Person search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length < 2 || selectedPerson) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await personService.search(searchQuery);
        setSearchResults(response.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Person search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, selectedPerson]);

  const selectPerson = (person) => {
    setSelectedPerson(person);
    setShowDropdown(false);
    setSearchQuery('');
    setFirstName(person.first_name || '');
    setLastName(person.last_name || '');
    setCompanyName(person.company_name || '');
    setPersonType(person.person_type || 'individual');
    // Load their existing contacts for display
    setContacts((person.contacts || []).map(c => ({
      id: c.id,
      contact_type: c.contact_type,
      value: c.value,
      label: c.label || '',
      existing: true,
    })));
  };

  const clearSelection = () => {
    setSelectedPerson(null);
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setContacts([]);
    setPersonType('individual');
  };

  const addContact = () => {
    setContacts(prev => [...prev, { contact_type: 'email', value: '', label: '', existing: false }]);
  };

  const updateContact = (index, field, value) => {
    setContacts(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const removeContact = (index) => {
    setContacts(prev => prev.filter((_, i) => i !== index));
  };

  // Relationship helpers
  const addRelationship = () => {
    setRelationships(prev => [...prev, {
      relationship_type: 'spouse',
      notes: '',
      searchQuery: '',
      selectedPerson: null,
      searchResults: [],
      showDropdown: false,
      searchLoading: false,
    }]);
  };

  const updateRelationship = (index, field, value) => {
    setRelationships(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const removeRelationship = (index) => {
    setRelationships(prev => prev.filter((_, i) => i !== index));
  };

  const searchRelationshipPerson = async (index, query) => {
    updateRelationship(index, 'searchQuery', query);
    if (query.length < 2) {
      updateRelationship(index, 'searchResults', []);
      updateRelationship(index, 'showDropdown', false);
      return;
    }
    updateRelationship(index, 'searchLoading', true);
    try {
      const response = await personService.search(query);
      setRelationships(prev => prev.map((r, i) => i === index ? {
        ...r,
        searchResults: response.data || [],
        showDropdown: true,
        searchLoading: false,
      } : r));
    } catch (err) {
      updateRelationship(index, 'searchLoading', false);
    }
  };

  const selectRelationshipPerson = (index, person) => {
    setRelationships(prev => prev.map((r, i) => i === index ? {
      ...r,
      selectedPerson: person,
      showDropdown: false,
      searchQuery: '',
    } : r));
  };

  const handleSubmit = async (e, skipConflict = false, conflictCheckId = null, overrideReason = null) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const partyData = {
        role,
        notes: notes || undefined,
        skip_conflict_check: skipConflict,
        conflict_check_id: conflictCheckId || undefined,
        override_reason: overrideReason || undefined,
      };

      if (role === 'client' && selectedClient) {
        partyData.client_id = selectedClient.id;
      } else if (selectedPerson) {
        partyData.person_id = selectedPerson.id;
      } else {
        partyData.person_type = personType;
        partyData.first_name = firstName || undefined;
        partyData.last_name = lastName || undefined;
        partyData.company_name = companyName || undefined;
        partyData.contacts = contacts
          .filter(c => !c.existing && c.value.trim())
          .map(c => ({
            contact_type: c.contact_type,
            value: c.value.trim(),
            label: c.label || undefined,
            is_primary: false,
          }));
      }

      const response = await casePartyService.addParty(caseId, partyData);
      const personId = response.data?.person_id || response.data?.person?.id;

      // Save relationships as follow-up calls
      if (personId && relationships.length > 0) {
        const relPromises = relationships
          .filter(r => r.selectedPerson)
          .map(r => personService.addRelationship(personId, {
            person_id_b: r.selectedPerson.id,
            relationship_type: r.relationship_type,
            notes: r.notes || undefined,
          }).catch(err => console.error('Failed to add relationship:', err)));
        await Promise.all(relPromises);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        // Conflict detected
        const detail = err.response.data.detail;
        setConflictData({
          conflictCheckId: detail.conflict_check_id,
          matches: detail.matches || [],
        });
        setShowConflictDialog(true);
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to add party');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConflictOverride = (conflictCheckId, reason) => {
    setShowConflictDialog(false);
    handleSubmit(null, true, conflictCheckId, reason);
  };

  if (!isOpen) return null;

  const isValid = (role === 'client' && selectedClient) || selectedPerson || firstName || lastName || companyName;

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center z-50"
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
      >
        <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-50 -z-10" aria-hidden="true"></div>
        <div
          className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">Add Party to Case</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={(e) => handleSubmit(e)} className="flex-1 overflow-y-auto p-6 space-y-4">
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
            )}

            {/* Role — shown first so client picker can toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <select
                value={role}
                onChange={(e) => { setRole(e.target.value); setSelectedClient(null); setClientSearchQuery(''); }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Client Picker — shown when role is 'client' */}
            {role === 'client' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Client *</label>
                {selectedClient ? (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                    <span className="text-sm text-emerald-800">
                      {[selectedClient.first_name, selectedClient.last_name].filter(Boolean).join(' ')}
                      {selectedClient.company_name && ` (${selectedClient.company_name})`}
                    </span>
                    <button type="button" onClick={() => setSelectedClient(null)} className="text-emerald-600 hover:text-emerald-800">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={clientSearchQuery}
                        onChange={(e) => setClientSearchQuery(e.target.value)}
                        placeholder="Search clients..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div className="mt-1 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                      {clientSearchLoading ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">Searching...</div>
                      ) : clients.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">No clients found</div>
                      ) : clients.map(client => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => setSelectedClient(client)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {[client.first_name, client.last_name].filter(Boolean).join(' ')}
                            </div>
                            {client.company_name && <div className="text-gray-500 text-xs">{client.company_name}</div>}
                            {client.email && <div className="text-gray-400 text-xs">{client.email}</div>}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Person Search — hidden when role is 'client' */}
            {role !== 'client' && (<>
            <div className="relative" ref={searchRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Existing Persons
              </label>
              {selectedPerson ? (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                  <span className="text-sm text-blue-800">
                    {[selectedPerson.first_name, selectedPerson.last_name].filter(Boolean).join(' ')}
                    {selectedPerson.company_name && ` (${selectedPerson.company_name})`}
                  </span>
                  <button type="button" onClick={clearSelection} className="text-blue-600 hover:text-blue-800">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or company..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  {searchLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                    </div>
                  )}
                </div>
              )}

              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((person) => (
                    <button
                      key={person.id}
                      type="button"
                      onClick={() => selectPerson(person)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">
                        {[person.first_name, person.last_name].filter(Boolean).join(' ')}
                      </div>
                      {person.company_name && (
                        <div className="text-gray-500 text-xs">{person.company_name}</div>
                      )}
                      {person.contacts?.length > 0 && (
                        <div className="text-gray-400 text-xs">
                          {person.contacts.slice(0, 2).map(c => c.value).join(', ')}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {showDropdown && searchResults.length === 0 && searchQuery.length >= 2 && !searchLoading && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No persons found. Enter details below to create a new one.
                  </div>
                </div>
              )}
            </div>

            {!selectedPerson && (
              <div className="border-t pt-4 text-xs text-gray-500 -mb-2">
                Or create a new person:
              </div>
            )}

            {/* Person Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={personType}
                onChange={(e) => setPersonType(e.target.value)}
                disabled={!!selectedPerson}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
              >
                <option value="individual">Individual</option>
                <option value="business">Business</option>
                <option value="organization">Organization</option>
              </select>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={!!selectedPerson}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={!!selectedPerson}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={!!selectedPerson}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
              />
            </div>

            {/* Contacts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Contact Methods</label>
                {!selectedPerson && (
                  <button
                    type="button"
                    onClick={addContact}
                    className="flex items-center gap-1 text-xs text-yellow-700 hover:text-yellow-800"
                  >
                    <Plus size={14} /> Add Contact
                  </button>
                )}
              </div>
              {contacts.length === 0 && (
                <p className="text-xs text-gray-400">No contacts added yet.</p>
              )}
              {contacts.map((contact, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <select
                    value={contact.contact_type}
                    onChange={(e) => updateContact(index, 'contact_type', e.target.value)}
                    disabled={contact.existing}
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-32 disabled:bg-gray-100"
                  >
                    {CONTACT_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={contact.value}
                    onChange={(e) => updateContact(index, 'value', e.target.value)}
                    disabled={contact.existing}
                    placeholder={contact.contact_type === 'email' ? 'email@example.com' : '(555) 123-4567'}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm disabled:bg-gray-100"
                  />
                  {!contact.existing && (
                    <button type="button" onClick={() => removeContact(index)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* End of person-only section */}
            </>)}

            {/* Relationships — only for person-type parties */}
            {role !== 'client' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <Link2 size={14} /> Relationships
                </label>
                <button
                  type="button"
                  onClick={addRelationship}
                  className="flex items-center gap-1 text-xs text-yellow-700 hover:text-yellow-800"
                >
                  <Plus size={14} /> Add Relationship
                </button>
              </div>
              {relationships.length === 0 && (
                <p className="text-xs text-gray-400">No relationships added yet.</p>
              )}
              {relationships.map((rel, index) => (
                <div key={index} className="mb-3 p-3 bg-gray-50 rounded-lg space-y-2">
                  {/* Person search */}
                  <div className="relative">
                    {rel.selectedPerson ? (
                      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-1.5">
                        <span className="text-sm text-blue-800">
                          {[rel.selectedPerson.first_name, rel.selectedPerson.last_name].filter(Boolean).join(' ')}
                          {rel.selectedPerson.company_name && ` (${rel.selectedPerson.company_name})`}
                        </span>
                        <button type="button" onClick={() => updateRelationship(index, 'selectedPerson', null)} className="text-blue-600 hover:text-blue-800">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={rel.searchQuery}
                          onChange={(e) => searchRelationshipPerson(index, e.target.value)}
                          placeholder="Search person..."
                          className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        />
                        {rel.searchLoading && (
                          <div className="absolute right-2.5 top-2">
                            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    )}
                    {rel.showDropdown && rel.searchResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {rel.searchResults.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => selectRelationshipPerson(index, p)}
                            className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                          >
                            <span className="font-medium text-gray-900">
                              {[p.first_name, p.last_name].filter(Boolean).join(' ')}
                            </span>
                            {p.company_name && (
                              <span className="text-gray-500 text-xs ml-1">({p.company_name})</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={rel.relationship_type}
                      onChange={(e) => updateRelationship(index, 'relationship_type', e.target.value)}
                      className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-40"
                    >
                      {RELATIONSHIP_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={rel.notes}
                      onChange={(e) => updateRelationship(index, 'notes', e.target.value)}
                      placeholder="Notes (optional)"
                      className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                    />
                    <button type="button" onClick={() => removeRelationship(index)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Case-specific notes about this party..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
              />
            </div>
          </form>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={(e) => handleSubmit(e)}
              disabled={loading || !isValid}
              className="px-4 py-2 text-sm text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Party'}
            </button>
          </div>
        </div>
      </div>

      {/* Conflict Warning Dialog */}
      <ConflictWarningDialog
        isOpen={showConflictDialog}
        onClose={() => setShowConflictDialog(false)}
        onOverride={handleConflictOverride}
        conflicts={conflictData?.matches || []}
        conflictCheckId={conflictData?.conflictCheckId}
        loading={loading}
      />
    </>
  );
};

export default AddPartyModal;
